'use client'

import {
  Body,
  CardGrid,
  Container,
  GlassCard,
  GlassCardBody,
  Reveal,
  Section,
  SectionHeader,
  Stagger,
  ThemeImageClient,
} from 'design-studio'
import {
  SL_GLASS_BODY,
  SL_GLASS_SURFACE,
  SL_GLASS_TITLE,
} from '@/components/theme'

const REASONING_TYPES = [
  {
    title: 'Consistency checking',
    description:
      'Check whether requirements, scenarios, and modeled behavior are mutually consistent.',
  },
  {
    title: 'Reachability analysis',
    description:
      'Determine whether intended states and transitions are actually possible, and whether some states are unreachable.',
  },
  {
    title: 'Contradiction detection',
    description:
      'Find conflicts between requirements, constraints, scenarios, and modeled behavior.',
  },
  {
    title: 'Edge-case discovery',
    description:
      'Surface unusual, boundary, and failure-prone behaviors that are easy to miss with prompt-local reasoning.',
  },
  {
    title: 'Counterexample generation',
    description:
      'If an assumption or property is false, produce a concrete case that breaks it.',
  },
  {
    title: 'Coverage and test generation',
    description:
      'Identify uncovered behavioral regions and generate high-value tests from them.',
  },
]

export function SectionReasoning() {
  return (
    <Section
      id="reasoning"
      className="relative scroll-mt-19 py-16 sm:pt-24 sm:pb-32"
    >
      <Container className="relative z-10">
        <Stagger className="space-y-10">
          <Reveal className="mx-auto max-w-6xl">
            <SectionHeader
              pretitle="Mathematical reasoning"
              pretitleColor="text-teal-500"
              title="What Mathematical Reasoning Takes Place"
              description="Once SpecLogician has helped developers and LLM coding agents discover shared mathematical structure, they can invoke automated reasoning over the model."
            />
          </Reveal>

          <Reveal className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-2">
            <figure className="flex flex-col gap-3">
              <ThemeImageClient
                lightSrc="/images/dashboard/sl-reachability-graph-light.png"
                darkSrc="/images/dashboard/sl-reachability-graph-dark.png"
                alt="Reachability graph with colored nodes for initial, observed, goal, bad, and sink states connected by labelled transitions"
                className="w-full overflow-hidden rounded-xl opacity-85 shadow-md"
                width={834}
                height={774}
              />
              <Body size="sm" tone="muted" className="text-center">
                Reachability graph — edges are actions and transitions that may
                or may not correspond to existing scenarios
              </Body>
            </figure>
            <figure className="flex flex-col gap-3">
              <ThemeImageClient
                lightSrc="/images/dashboard/sl-reachability-transition-light.png"
                darkSrc="/images/dashboard/sl-reachability-transition-dark.png"
                alt="A selected transition showing the producing scenario, witness pre-state, action, and post-state predicate valuation"
                className="w-full overflow-hidden rounded-xl opacity-85 shadow-md"
                width={1060}
                height={872}
              />
              <Body size="sm" tone="muted" className="text-center">
                Click an edge for the concrete witness — pre-state, action,
                post-state.
              </Body>
            </figure>
          </Reveal>

          <Reveal className="mx-auto max-w-7xl">
            <CardGrid columns={3} gap="sm">
              {REASONING_TYPES.map((item) => (
                <GlassCard
                  key={item.title}
                  title={item.title}
                  titleClassName={SL_GLASS_TITLE}
                  className={SL_GLASS_SURFACE}
                >
                  <GlassCardBody className={SL_GLASS_BODY}>
                    {item.description}
                  </GlassCardBody>
                </GlassCard>
              ))}
            </CardGrid>
          </Reveal>
        </Stagger>
      </Container>
    </Section>
  )
}
